import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";

const TABS = [
	"All",
	"Pending Orders",
	"Declined Orders",
	"Preparing for Delivery",
	"Placed for Delivery",
	"Delivering",
	"Delivered",
	"Confirmed Delivery",
];

const TAB_ENDPOINTS = {
	"All": `${process.env.REACT_APP_API_URL}/api/orders/all`,
	"Pending Orders": `${process.env.REACT_APP_API_URL}/api/orders/pending`,
	"Declined Orders": `${process.env.REACT_APP_API_URL}/api/orders/declined`,
	"Preparing for Delivery": `${process.env.REACT_APP_API_URL}/api/orders/preparing-for-delivery`,
	"Placed for Delivery": `${process.env.REACT_APP_API_URL}/api/orders/placed-for-delivery`,
	"Delivering": `${process.env.REACT_APP_API_URL}/api/orders/delivering`,
	"Delivered": `${process.env.REACT_APP_API_URL}/api/orders/delivered`,
	"Confirmed Delivery": `${process.env.REACT_APP_API_URL}/api/orders/confirmed`,
};

const Header = ({ activeTab, setActiveTab, totalCount }) => {
	const navigate = useNavigate();

	return (
		<header className="header top-0 left-0 right-0 z-20 bg-white shadow-sm gap-[14px]">
			<div className="flex items-center justify-between gap-x-[14px] p-2">
				<button
					type="button"
					onClick={() => navigate(-1)}
					className="h-9 w-9 rounded-full flex items-center justify-center"
					aria-label="Go back"
				>
					<FiArrowLeft className="h-5 w-5 text-gray-700" />
				</button>
				<h1 className="text-base font-semibold text-gray-900">
					Orders <span>({totalCount})</span>
				</h1>
				<div className="h-9 w-9" />
			</div>

			<div>
				<div className="flex gap-3 overflow-x-auto no-scrollbar px-2">
					{TABS.map((tab) => {
						const isActive = activeTab === tab;
						return (
							<button
								key={tab}
								type="button"
								onClick={() => setActiveTab(tab)}
								className={`whitespace-nowrap px-1 py-1 text-sm transition ${
									isActive
										? "bg-[#A3E7C1] border border-[#19C463] text-[#000000]"
										: "border border-[#000000] text-gray-700 hover:border-[#19C463] hover:text-[#19C463]"
								}`}
								style={{ borderRadius: "4px", borderWidth: "1px" }}
							>
								{tab}
							</button>
						);
					})}
				</div>
			</div>
		</header>
	);
};

const ProductCard = ({ product, onClick }) => (
	<div
		className="bg-white shadow-sm flex flex-col gap-2"
		style={{ borderRadius: '8px', padding: '4px 6px' }}
		onClick={onClick}
		role="button"
		tabIndex={0}
		onKeyDown={(e) => {
			if (e.key === "Enter" || e.key === " ") {
				onClick?.();
			}
		}}
	>
		<div className="flex gap-4">
			<img
				loading="lazy"
				src={product.image || "https://via.placeholder.com/80"}
				alt={product.name}
				className="h-20 w-20 rounded-xl object-cover"
			/>
			   <div className="flex-1 text-left p-0.5">
				   <p className="text-sm font-semibold text-gray-900 p-0.5 mt-0" style={{ fontSize: '18px', fontWeight: 600 }}>{product.name}</p>
									   <p className="text-base font-bold text-gray-900" style={{ fontSize: '20px', fontWeight: 600, padding: '2px' }}>
										<span>₦{Number(product.price || 0).toLocaleString("en-NG")}</span>
									</p>
				   <p className="text-xs text-gray-600" style={{ padding: '2px', fontSize: '16px', fontWeight: 600, color: '#000000' }}>Delivery in 3 days</p>
				   <p className="text-xs text-gray-600" style={{ padding: '2px', fontSize: '16px', color: '#000000', fontWeight: 600 }}>
					Product Status: <span>{product.status}</span>
				</p>
				   <button
					   type="button"
					   onClick={(e) => {
						 e.stopPropagation();
						 onClick?.();
					   }}
					   className="text-sm font-semibold text-[#19C463]" style={{ textAlign: 'right', padding: '2px', fontSize: '16px', fontWeight: 600, width: '100%' }}
				   />
			</div>
		</div>
	</div>
);

const OrderGroup = ({ order, onProductClick }) => (
	<div className="mt-0 gap-2 flex flex-col">
		   <div className="flex items-center justify-between text-sm text-gray-600">
			   <span style={{ fontSize: '18px', fontWeight: 600, padding: '4px', color: '#000000' }}>{order.date}</span>
			   <span style={{ fontSize: '18px', fontWeight: 600, padding: '4px', color: '#000000' }}>{order.time}</span>
		   </div>
		<div className="gap-2.5 flex flex-col">
			{order.products.map((product) => (
				<ProductCard
					key={product.id}
					product={product}
					onClick={() => onProductClick?.(product)}
				/>
			))}
		</div>
	</div>
);

const OrdersScreen = () => {
	const [activeTab, setActiveTab] = useState(TABS[0]);
	const [ordersData, setOrdersData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const navigate = useNavigate();

	useEffect(() => {
		const fetchOrders = async () => {
			const url = TAB_ENDPOINTS[activeTab];
			if (!url) return;
			setLoading(true);
			setError("");
			try {
				const token = localStorage.getItem("userToken");
				const res = await fetch(url, {
					headers: token ? { Authorization: `Bearer ${token}` } : {},
				});
				const data = await res.json();
				if (!data.success) {
					throw new Error(data.message || "Failed to load orders");
				}
				const normalized = (data.data || []).map((order) => {
					const createdAt = order.createdAt ? new Date(order.createdAt) : null;
					const date = createdAt ? createdAt.toLocaleDateString() : "";
					const time = createdAt ? createdAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";
					return {
						id: order._id,
						date,
						time,
					products: (order.items || []).map((item) => ({
						id: item.orderItemId || item._id,
						orderItemId: item.orderItemId || item._id,
						name: item.productName,
						price: item.price,
						status: item.orderStatus,
						image: item.productImages?.[0] || item.productImage || "https://via.placeholder.com/80",
					})),
					};
				});
				setOrdersData(normalized);
			} catch (err) {
				setError(err.message);
				setOrdersData([]);
			} finally {
				setLoading(false);
			}
		};

		fetchOrders();
	}, [activeTab]);

	const totalCount = useMemo(
		() => ordersData.reduce((sum, order) => sum + (order.products?.length || 0), 0),
		[ordersData]
	);

	return (
		<div className="min-h-screen bg-[#F1F3F2] text-gray-900">
			<Header activeTab={activeTab} setActiveTab={setActiveTab} totalCount={totalCount} />
			   <main className="px-2 pt-5 pb-8" style={{ gap: '20px', display: 'flex', flexDirection: 'column' }}>
				{loading ? (
					<div className="text-center text-gray-500">Loading orders...</div>
				) : error ? (
					<div className="text-center text-red-500">{error}</div>
				) : ordersData.length === 0 ? (
					<div className="text-center text-gray-500">No orders found.</div>
				) : (
					ordersData.map((order) => (
						<OrderGroup
							key={order.id}
							order={order}
							onProductClick={(product) => {
								if (!product?.orderItemId && !product?.id) return;
								navigate(`/view-order/${product.orderItemId || product.id}`);
							}}
						/>
					))
				)}
			</main>
		</div>
	);
};

export default OrdersScreen;

